import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../../../services/api/resources";
import classes from "./charts.module.css";
import HeaderTitle from "../../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../../components/common/SearchBar/SearchBar";
import { Box, IconButton, TablePagination } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useSelector } from "react-redux";

const normalizeText = (value) => String(value || "").toLowerCase().trim();

const normalizeESignStatus = (value) => {
  const normalized = normalizeText(value);
  if (normalized === "signed") return "Signed";
  if (normalized === "not signed" || normalized === "notsigned" || normalized === "unsigned") {
    return "Not Signed";
  }
  return value || "-";
};

function PaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    onPageChange(event, lastPage);
  };

  const isBackDisabled = page === 0;
  const isNextDisabled = page >= Math.ceil(count / rowsPerPage) - 1;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={isBackDisabled} aria-label="first page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <LastPageIcon sx={{ fontSize: 24 }} /> : <FirstPageIcon sx={{ fontSize: 24 }} />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={isBackDisabled} aria-label="previous page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <KeyboardArrowRight sx={{ fontSize: 24 }} /> : <KeyboardArrowLeft sx={{ fontSize: 24 }} />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={isNextDisabled} aria-label="next page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <KeyboardArrowLeft sx={{ fontSize: 24 }} /> : <KeyboardArrowRight sx={{ fontSize: 24 }} />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={isNextDisabled} aria-label="last page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <FirstPageIcon sx={{ fontSize: 24 }} /> : <LastPageIcon sx={{ fontSize: 24 }} />}
      </IconButton>
    </Box>
  );
}

const PendingESignatureTable = () => {
  const Data = useSelector((state) => state.loginData.value);
  const user_token = Data?.user?.token || Data?.user_token;
  const [vendorInvoiceData, setVendorInvoiceData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);

  const rowsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.post(API_ENDPOINT.get_all_invoices, {
          org_token: user_token,
          page: currentPage + 1,
        });
        setVendorInvoiceData(
          res?.data?.invoices || res?.data?.results?.invoices || []
        );
        setTotalCount(Number(res?.data?.total_count || res?.data?.count || 0));
      } catch (error) {
        console.error("Error fetching invoice data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, user_token]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const filteredData = vendorInvoiceData.filter((invoice) => {
    const query = normalizeText(searchTerm);
    const nameMatch = normalizeText(invoice?.vendor_name).includes(query);
    const invoiceMatch = normalizeText(invoice?.vendor_code).includes(query);
    const amountMatch = normalizeText(invoice?.amount).includes(query);

    const normalizedStatus = normalizeESignStatus(invoice?.esign_status);
    const statusMatch =
      normalizeText(statusFilter) === "all" ||
      normalizeText(normalizedStatus) === normalizeText(statusFilter);

    return (nameMatch || invoiceMatch || amountMatch) && statusMatch;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === "amount") {
      const amountA = Number(a.amount || 0);
      const amountB = Number(b.amount || 0);
      return sortConfig.direction === "asc"
        ? amountA - amountB
        : amountB - amountA;
    }

    if (sortConfig.key === "esign_status") {
      const statusA = normalizeESignStatus(a.esign_status);
      const statusB = normalizeESignStatus(b.esign_status);
      return sortConfig.direction === "asc"
        ? statusA.localeCompare(statusB)
        : statusB.localeCompare(statusA);
    }

    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    if (typeof valA === "string" || typeof valB === "string") {
      return sortConfig.direction === "asc"
        ? String(valA || "").localeCompare(String(valB || ""))
        : String(valB || "").localeCompare(String(valA || ""));
    }

    if (typeof valA === "number" || typeof valB === "number") {
      return sortConfig.direction === "asc"
        ? Number(valA || 0) - Number(valB || 0)
        : Number(valB || 0) - Number(valA || 0);
    }

    if (!isNaN(Date.parse(valA)) && !isNaN(Date.parse(valB))) {
      return sortConfig.direction === "asc"
        ? new Date(valA) - new Date(valB)
        : new Date(valB) - new Date(valA);
    }

    return 0;
  });

  const currentItems = sortedData;

  return (
    <div className={classes.tablecontainer} style={{ marginTop: "1rem" }}>
      <HeaderTitle titleText={"Awaiting E-Signature by Merchant"} />

      <div className={classes.filterWrapper}>
        <SearchBar
          customClass={classes.tableSearch}
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(0);
          }}
        />

        <div className={classes.filtercontainer}>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(0);
            }}
            className={classes.filterSelect}
          >
            <option value="All">All Status</option>
            <option value="Signed">Signed</option>
            <option value="Not Signed">Not Signed</option>
          </select>
        </div>
      </div>

      <table className={`${classes.statustable} ${classes.buyerHeaderTable}`}>
        <thead>
          <tr>
            <th onClick={() => handleSort("vendor_code")}>
              Supplier Invoice ID {sortConfig.key === "vendor_code" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("vendor_name")}>
              Merchant Name {sortConfig.key === "vendor_name" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("amount")}>
              Amount {sortConfig.key === "amount" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("created_on")}>
              Uploaded On {sortConfig.key === "created_on" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("esign_status")}>
              Status {sortConfig.key === "esign_status" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className={classes.loadingRow}>Loading data...</td>
            </tr>
          ) : currentItems.length === 0 ? (
            <tr>
              <td colSpan="5" className="no-data">No data found</td>
            </tr>
          ) : (
            currentItems.map((invoice) => (
              <tr key={invoice.id}>
                <td>{invoice.vendor_code || "-"}</td>
                <td>{invoice.vendor_name || "-"}</td>
                <td>{"\u20B9"}{Number(invoice.amount || 0).toLocaleString("en-IN")}</td>
                <td>{invoice.created_on ? new Date(invoice.created_on).toLocaleDateString() : "-"}</td>
                <td>{normalizeESignStatus(invoice.esign_status)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {(searchTerm || statusFilter !== "All"
        ? filteredData.length > rowsPerPage
        : totalCount > rowsPerPage) && (
        <TablePagination
          component="div"
          count={
            searchTerm || statusFilter !== "All"
              ? filteredData.length
              : totalCount
          }
          page={currentPage}
          onPageChange={(event, newPage) => setCurrentPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[rowsPerPage]}
          showFirstButton
          showLastButton
          ActionsComponent={PaginationActions}
          sx={{
            "& .MuiTablePagination-toolbar": { fontSize: "14px" },
            "& .MuiTablePagination-selectLabel": { fontSize: "14px" },
            "& .MuiTablePagination-displayedRows": { fontSize: "14px" },
            "& .MuiTablePagination-select": { fontSize: "14px" },
          }}
        />
      )}
    </div>
  );
};

export default PendingESignatureTable;
